import * as React from 'react';
import { useEffect, useRef, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { loadTranslationsAction } from './loadTranslationsAction';
import { portalLanguagesAction } from './portalLanguagesAction';
import { useMount } from '../core/hooks/useMount';
import { setLocaleAction } from './setLocaleAction';
import { getSupportedLanguage } from './module';
import { useAppState } from '../core/redux/hooks';
import { loadProjMsgsAction } from '../project/messages/loadProjMsgsAction';
import { useRoutes } from '../navigation/routes';
import { passwordRequirementMessageAction } from '../auth/accountSetup/api/passwordRequirementMessageAction';
import { getSelectedQuestionsAction } from '../project/createSurvey/api/getSelectedQuestionsAction';
import { getCompetenciesAndQuestionsAction } from '../project/createSurvey/api/getCompetenciesAndQuestionsAction';
import { getDevPlanItemsAction } from '../project/devPlan/api/getDevPlanItemsAction';
import { getDevPlanFieldsAndAnswersAction } from '../project/devPlan/devPlanAction/api/getDevPlanFieldsAndAnswersAction';
import { loadParticipantRequirementsAction } from '../project/requirements/loadParticipantRequirementsAction';
import { loadProjectsAction } from '../project/loadProjectsAction';
import { loadProjectSummaryAction } from '../project/loadProjectSummaryAction';
import { loadDevResourcesAction } from '../project/resources/loadDevResourcesAction';
import { loadProjectResourcesAction } from '../project/resources/loadProjectResourcesAction';
import { participantRatersAction } from '../project/survey/raters/participantRatersAction';
import { goalsAnswersAction } from '../project/setGoals/goalsAnswersAction';
import { raterLevelsAction } from '../project/survey/raters/raterLevelsAction';
import { useCurrentUser } from '../auth/hooks';
import { useCurrentCollaborator } from '../project/devPlan/collaboration/hooks';
import { allLanguagesAction } from './allLanguagesAction';


/**
 * TranslationLoader: a component that listens for Redux locale changes and loads missing translations for newly
 * selected locales.
 *
 * PureComponent: true
 * Redux Connected: true
 */
const TranslationLoader: React.FC = () => {
  const dispatch   = useDispatch();
  const langs      = useAppState(state => state.locale.portalLanguages);
  const language   = useAppState(state => state.locale.lang);
  const projectId  = useAppState(state => state.projectSummary?.projectId);
  const routes     = useRoutes();

  const devPlanProjectId = useAppState(state => state.projectSummary?.devPlanProjectId);

  const languageId    = useMemo(() => language.languageId, [language.languageId]);
  const currentUser   = useCurrentUser();

  const currentCollaborator = useCurrentCollaborator();

  const participantId = useMemo(
    () => currentUser?.personId ?? currentCollaborator?.currentProject.participantId ?? 0,
    [currentCollaborator, currentUser],
  );

  const questions = useAppState(state => state.selectedQuestions?.[`${projectId}`] ?? []);
  const compQ = useAppState(state => state.competenciesAndQuestions?.[`${projectId}`] ?? []);
  const devItems = useAppState(state => state.devPlan?.items ?? []);
  const fieldsAnswers = useAppState(state => state.devPlan?.fieldsAnswers);
  const requirements = useAppState(state => state.participantRequirements);
  const projects = useAppState(state => state.projects);
  const project = useAppState(state => state.projectSummary);
  const devResources = useAppState(state => state.devResources);
  const custResources = useAppState(state => state.projectResources);
  const raters = useAppState(state => state.projectRaters);
  const raterLevels = useAppState(state => state.projectRaterLevels);
  const goals = useAppState(state => state.goalsAnswers);

  const questionsRef = useRef(questions);
  useEffect(() => { questionsRef.current = questions;}, [questions, questionsRef]);

  const compQRef = useRef(compQ);
  useEffect(() => { compQRef.current = compQ;}, [compQ, compQRef]);

  const devItemsRef = useRef(devItems);
  useEffect(() => { devItemsRef.current = devItems;}, [devItems, devItemsRef]);

  const fieldsAnswersRef = useRef(fieldsAnswers);
  useEffect(() => { fieldsAnswersRef.current = fieldsAnswers;}, [fieldsAnswers, fieldsAnswersRef]);

  const requirementsRef = useRef(requirements);
  useEffect(() => { requirementsRef.current = requirements;}, [requirements, requirementsRef]);

  const projectsRef = useRef(projects);
  useEffect(() => { projectsRef.current = projects;}, [projects, projectsRef]);

  const projectRef = useRef(project);
  useEffect(() => { projectRef.current = project;}, [project, projectRef]);

  const devResourcesRef = useRef(devResources);
  useEffect(() => { devResourcesRef.current = devResources;}, [devResources, devResourcesRef]);

  const custResourcesRef = useRef(custResources);
  useEffect(() => { custResourcesRef.current = custResources;}, [custResources, custResourcesRef]);

  const ratersRef = useRef(raters);
  useEffect(() => { ratersRef.current = raters;}, [raters, ratersRef]);

  const raterLevelsRef = useRef(raterLevels);
  useEffect(() => { raterLevelsRef.current = raterLevels;}, [raterLevels, raterLevelsRef]);

  const goalsRef = useRef(goals);
  useEffect(() => { goalsRef.current = goals;}, [goals, goalsRef]);

  // Selected Language
  useMount(() => {
      dispatch(portalLanguagesAction);
      dispatch(allLanguagesAction);
      dispatch(
        setLocaleAction(getSupportedLanguage(langs, navigator.languages || [navigator.language]), undefined),
      );
    },
  );

  // Messages
  useEffect(
    () => { dispatch(loadTranslationsAction(routes.Root, languageId)); },
    [dispatch, language, languageId, routes.Root],
  ); // message/portal

  useEffect(
    () => {
      if (projectId) {
        dispatch(loadProjMsgsAction(languageId, projectId));
      }
    },
    [dispatch, languageId, projectId],
  ); // message/project

  useEffect(
    () => {
      if (projectId && currentUser) { dispatch(passwordRequirementMessageAction); }
    },
    [currentUser, dispatch, language, projectId],
  ); // message/passwordRequirements


  // Queries that use language id
  useEffect(
    () => {
      if (projectId && questionsRef.current) { dispatch(getSelectedQuestionsAction(projectId, participantId)); }
    },
    [dispatch, language, projectId, participantId, questionsRef],
  ); // createsurvey/getSelectedQuestions

  useEffect(
    () => {
      if (projectId && currentUser && compQRef.current) { dispatch(getCompetenciesAndQuestionsAction(projectId)); }
    },
    [dispatch, language, projectId, compQRef, currentUser],
  ); // createsurvey/getCompetenciesAndQuestions

  useEffect(
    () => {
      if (devPlanProjectId && devItemsRef.current) { dispatch(getDevPlanItemsAction(devPlanProjectId, participantId)); }
    },
    [dispatch, language, devPlanProjectId, participantId, devItemsRef],
  ); // developmentplan/items

  useEffect(
    () => {
      if (devPlanProjectId && fieldsAnswersRef.current) {
        dispatch(getDevPlanFieldsAndAnswersAction(devPlanProjectId, participantId));
      }
    },
    [dispatch, language, devPlanProjectId, participantId, fieldsAnswersRef],
  ); // developmentplan/fieldsAndAnswers

  useEffect(
    () => {
      if (projectId && requirementsRef.current) {
        dispatch(loadParticipantRequirementsAction(projectId, participantId, languageId));
      }
    },
    [dispatch, language, projectId, participantId, languageId, requirementsRef],
  ); // participant/requirements

  useEffect(
    () => {
      if (projectId && projectsRef.current) { dispatch(loadProjectsAction(participantId)); }
    },
    [dispatch, language, projectId, participantId, projectsRef],
  ); // project/portalProjects

  useEffect(
    () => {
      if (projectId && projectRef.current) { dispatch(loadProjectSummaryAction(projectId, participantId)); }
    },
    [dispatch, language, projectId, participantId, projectRef],
  ); // project/participantProject

  useEffect(
    () => {
      if (projectId && devResourcesRef.current) { dispatch(loadDevResourcesAction(projectId)); }
    },
    [dispatch, language, projectId, devResourcesRef],
  ); // project/participantProject

  useEffect(
    () => {
      if (projectId && custResourcesRef.current) { dispatch(loadProjectResourcesAction(projectId)); }
    },
    [dispatch, language, projectId, custResourcesRef],
  ); // project/customResources

  useEffect(
    () => {
      if (projectId && ratersRef.current) { dispatch(participantRatersAction(projectId, participantId)); }
    },
    [dispatch, language, projectId, participantId, ratersRef],
  ); // rater/participantRaters

  useEffect(
    () => {
      if (projectId && raterLevelsRef.current) { dispatch(raterLevelsAction(projectId)); }
    },
    [dispatch, language, projectId, raterLevelsRef],
  ); // rater/raterLevels

  useEffect(
    () => {
      if (projectId && goalsRef.current) { dispatch(goalsAnswersAction(projectId, participantId, languageId)); }
    },
    [dispatch, languageId, projectId, participantId, goalsRef],
  ); // setgoals/getGoals

  /*
   email/inviteBodyPreview (these are in AppStateSessionStorageBridge)
   email/remindBodyPreview
   email/approverBodyPreview
   email/approvalBodyPreview
   email/templateAvailability (not used)
   */

  // Non-rendering component
  return null;
};

export default TranslationLoader;
